import styled from '@emotion/styled';
import { TextField } from '@mui/material';

interface ThemeProps {
  theme?: any;
  isDark?: boolean;
}

export const ViewContainer = styled.div<ThemeProps>`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : 'white')};
  width: 100%;
  transition: background-color 0.2s ease;
`;

export const AddContestantArea = styled.div<
  {
    shouldAjustHeight: boolean;
  } & ThemeProps
>`
  border-radius: 10px;
  width: 100%;
  padding: 16px;
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : '#fff')};
  display: flex;
  gap: 10px;
  height: ${({ shouldAjustHeight }) =>
    shouldAjustHeight ? 'max-content' : '100px'};
  cursor: pointer;
  flex-wrap: wrap;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : 'transparent')};
  transition: background-color 0.2s ease, border-color 0.2s ease;
`;

export const CustomItemButton = styled.div<
  {
    backgroundColor: string;
  } & ThemeProps
>`
  cursor: pointer;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ backgroundColor }) => `${backgroundColor}`};
  text-align: center;
  gap: 6px;
  width: fit-content;
  height: fit-content;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ isDark }) =>
      isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'};
  }
`;

export const CustomIcon = styled.img`
  width: 16px;
  height: 16px;
`;

export const CustomText = styled.div<ThemeProps>`
  font-family: Manrope;
  font-size: 14px;
  color: ${({ theme, isDark }) =>
    theme?.custom?.text || (isDark ? '#FFFFFF' : '#333')};
`;

export const ContestantItem = styled.div<
  {
    isSelected: boolean;
  } & ThemeProps
>`
  cursor: pointer;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ContestantItemText = styled.div<ThemeProps>`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme, isDark }) =>
    theme?.custom?.text || (isDark ? '#FFFFFF' : '#000')};
  margin-left: 10px;
  font-family: Manrope;
`;

export const ContestantItemCheckIcon = styled.img`
  width: 20px;
  height: 20px;
`;

export const TabsContainer = styled.div<ThemeProps>`
  display: flex;
  justify-content: center;
  padding: 0 24px;
  margin-bottom: 20px;
`;

export const TypeList = styled.div`
  display: flex;
  gap: 30px;
  justify-content: center;
`;

export const TypeListItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

export const TypeListItemText = styled.div<
  {
    isActive: boolean;
  } & ThemeProps
>`
  font-weight: ${({ isActive }) => (isActive ? 600 : 400)};
  color: ${({ isActive, theme, isDark }) => {
    if (isActive) {
      return theme?.custom?.primary || '#FF6A00';
    }
    return theme?.custom?.textLight || (isDark ? '#B0B0B0' : '#737373');
  }};
  cursor: pointer;
  font-size: 14px;
  font-family: Manrope;
  transition: color 0.2s ease;
`;

export const TypeItemIndicator = styled.div<
  {
    isActive: boolean;
  } & ThemeProps
>`
  height: 2px;
  width: 100%;
  background-color: ${({ isActive, theme }) =>
    isActive ? theme?.custom?.primary || '#FF6A00' : 'transparent'};
  margin-top: 4px;
  transition: background-color 0.2s ease;
`;

export const ContestantItemContainer = styled.div<ThemeProps>`
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : '#f0f0f0')};
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : 'white')};
  transition: background-color 0.2s ease, border-color 0.2s ease,
    transform 0.1s ease;

  &:hover {
    background-color: ${({ theme, isDark }) =>
      theme?.custom?.background2 ||
      (isDark ? '#1E1E1E' : 'rgba(0, 0, 0, 0.02)')};
    border-color: ${({ theme }) => theme?.custom?.primary || '#FF6A00'};
    transform: translateY(-1px);
  }
`;

export const CountText = styled.div<ThemeProps>`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme, isDark }) =>
    theme?.custom?.text || (isDark ? '#FFFFFF' : '#000')};
  margin-bottom: 16px;
  font-family: Manrope;
`;

export const SearchWrapper = styled.div<ThemeProps>`
  display: flex;
  align-items: center;
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background2 || (isDark ? '#1E1E1E' : 'white')} !important;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 16px;
  margin-top: 8px;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : '#f0f0f0')};
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:focus-within {
    border-color: ${({ theme }) => theme?.custom?.primary || '#FF6A00'};
    box-shadow: ${({ isDark, theme }) =>
      isDark
        ? `0 0 0 2px ${theme?.custom?.primary || '#FF6A00'}20`
        : `0 0 0 2px ${theme?.custom?.primary || '#FF6A00'}30`};
  }
`;

export const SearchInput = styled(TextField)<ThemeProps>`
  flex: 1;

  .MuiInputBase-root {
    padding: 0;
    font-size: 14px;
    color: ${({ theme, isDark }) =>
      theme?.custom?.text || (isDark ? '#FFFFFF' : '#333')};
    font-family: Manrope;
    background-color: transparent !important;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiInputBase-input::placeholder {
    color: ${({ theme, isDark }) =>
      theme?.custom?.textLight || (isDark ? '#B0B0B0' : 'rgba(0, 0, 0, 0.5)')};
    opacity: 1;
  }

  .MuiInputBase-input {
    padding: 0;
    height: 24px;
    background-color: transparent !important;
  }

  /* Override any MUI default backgrounds */
  & .MuiInput-root,
  & .MuiInputBase-root,
  & .MuiTextField-root {
    background-color: transparent !important;
  }
`;

export const AddButtonText = styled.div<ThemeProps>`
  color: ${({ theme }) => theme?.custom?.primary || '#ff6a00'};
  font-size: 14px;
  font-weight: 500;
  text-align: right;
  cursor: pointer;
  margin-bottom: 16px;
  margin-top: 16px;
  font-family: Manrope;
  transition: color 0.2s ease, transform 0.1s ease;

  &:hover {
    color: ${({ theme }) => theme?.custom?.primaryDark || '#DC5C01'};
    transform: translateY(-1px);
  }
`;

export const ModalContent = styled.div<ThemeProps>`
  padding: 0 16px 16px 16px;
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : 'white')};
  transition: background-color 0.2s ease;
`;

export const ContestantsList = styled.div<ThemeProps>`
  max-height: 400px;
  overflow-y: auto;
  margin-top: 16px;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme, isDark }) =>
      theme?.custom?.background3 || (isDark ? '#2D2D2D' : '#f1f1f1')};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme, isDark }) =>
      theme?.custom?.textLight || (isDark ? '#555555' : '#c4c4c4')};
    border-radius: 4px;

    &:hover {
      background: ${({ theme, isDark }) =>
        theme?.custom?.text || (isDark ? '#666666' : '#a0a0a0')};
    }
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme, isDark }) =>
    isDark
      ? `${theme?.custom?.textLight || '#555555'} ${
          theme?.custom?.background3 || '#2D2D2D'
        }`
      : `${theme?.custom?.textLight || '#c4c4c4'} ${
          theme?.custom?.background3 || '#f1f1f1'
        }`};
`;

export const CheckboxContainer = styled.div<ThemeProps>`
  width: 20px;
  height: 20px;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#555555' : '#d7d9d6')};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : 'white')};
  transition: border-color 0.2s ease, background-color 0.2s ease;
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
